import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import SignatureModal from '../signature/SignatureModal';
import AgreementQuoteApproval from './AgreementQuoteApproval';
import {
  createAgreementLink,
  fetchAgreementDocument,
  signAgreementDocument,
  type AgreementDocument,
  type AgreementDocumentKind,
} from '../../data/serviceAgreements';
import { safeErrorMessage } from '../../utils/appErrors';
import './ServiceAgreements.css';

export function AgreementDocumentContent({ document: doc }: { document: AgreementDocument }) {
  const signed = doc.signature?.signed_payload?.kind ? doc.signature.signed_payload : doc;
  const location = signed.benefits?.location as { name?: string; address?: Record<string, string> } | undefined;
  const used = doc.visits?.filter((v) => v.allocated || v.status === 'completed').length ?? 0;
  return (
    <article className="agreement-document">
      <h2>{doc.title}</h2>
      <p>
        {signed.client_name} / {doc.status.replace(/_/g, ' ')}
      </p>
      {signed.price != null ? (
        <p>
          <strong>
            {Number(signed.price).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
          </strong>{' '}
          / {signed.benefits?.term_mode === 'dates' ? 'custom coverage dates' : `${signed.term_years} year(s)`}
        </p>
      ) : null}
      {doc.start_date ? (
        <p>
          Coverage: {doc.start_date} through {doc.end_date}
        </p>
      ) : null}
      {doc.warranty_start_date ? (
        <p>
          Installation warranty: {doc.warranty_start_date} through {doc.warranty_end_date}
        </p>
      ) : null}
      {doc.status === 'awaiting_installation' ? (
        <p>Coverage starts after customer completion sign-off and the included 90-day warranty.</p>
      ) : null}
      {location ? <p><strong>{location.name}</strong><br />{[location.address?.line1, location.address?.city, location.address?.state, location.address?.postal_code].filter(Boolean).join(', ')}</p> : null}
      {signed.scope ? <p>{signed.scope}</p> : null}
      {!location && signed.address ? <p>{signed.address}</p> : null}
      {signed.benefits ? (
        <dl className="agreement-benefits">
          {[
            'name',
            'description',
            'pm_visit_count',
            'pm_frequency_months',
            'parts_labor_discount_percent',
            'response_level',
            'onsite_support',
            'manufacturer_warranty',
            'loaner_equipment',
          ].map((key) =>
            signed.benefits?.[key] != null ? (
              <React.Fragment key={key}>
                <dt>{key.replace(/_/g, ' ')}</dt>
                <dd>{String(signed.benefits[key])}</dd>
              </React.Fragment>
            ) : null,
          )}
        </dl>
      ) : null}
      <div className="agreement-terms">{signed.terms}</div>
      {doc.visits ? (
        <section>
          <h3>PM visits</h3>
          <p>
            {doc.visits.length} allotted / {used} used /{' '}
            {
              doc.visits.filter(
                (v) => !v.allocated && !['completed', 'cancelled'].includes(v.status),
              ).length
            }{' '}
            remaining
          </p>
          {doc.visits.map((v) => (
            <p key={v.id}>
              Visit {v.sequence} / Due {v.due_date} /{' '}
              {v.status === 'scheduled' ? 'Available' : v.status.replace(/_/g, ' ')}
            </p>
          ))}
        </section>
      ) : null}
      {doc.signature ? (
        <section>
          <h3>Signed acceptance</h3>
          <p>
            {doc.signature.signer_name} / {new Date(doc.signature.signed_at).toLocaleString()}
          </p>
          <p>{doc.signature.statement}</p>
          {doc.signature.image ? (
            <img
              className="agreement-signature"
              src={doc.signature.image}
              alt="Customer signature"
            />
          ) : null}
          <p className="agreement-hash">Certificate: {doc.signature.record_hash}</p>
        </section>
      ) : null}
    </article>
  );
}

export default function AgreementDocumentView({
  kind: suppliedKind,
  targetId,
  readOnly = false,
}: {
  kind?: AgreementDocumentKind;
  targetId?: string;
  readOnly?: boolean;
}) {
  const params = useParams();
  const kind = suppliedKind ?? (params.kind as AgreementDocumentKind) ?? 'quote';
  const id = targetId ?? params.id;
  const token = params.token;
  const query = useQuery({
    queryKey: ['agreement-document', kind, id, token],
    queryFn: () => fetchAgreementDocument(kind, id, token),
    retry: false,
  });
  const [email, setEmail] = React.useState('');
  const [signing, setSigning] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [link, setLink] = React.useState('');
  const doc = query.data;
  const canSign =
    doc &&
    !doc.signature &&
    doc.kind === 'quote' && ['approved', 'sent'].includes(doc.status);
  const staffView = !token && !readOnly;
  // service_agreement_create_link refuses a quote until it is approved.
  const linkReady = doc?.kind !== 'quote' || ['approved', 'sent', 'accepted'].includes(doc.status);
  return (
    <>
      {staffView && doc?.client_id ? (
        <Link className="agreement-back-link" to={`/clients/${doc.client_id}/service`}>
          ‹ {doc.client_name || 'Customer'} › Service
        </Link>
      ) : null}
      {staffView && kind === 'quote' && id ? (
        <AgreementQuoteApproval quoteId={id} onApproved={() => void query.refetch()} />
      ) : null}
      <section className="agreement-surface">
        {query.isLoading ? <p>Loading document...</p> : null}
        {query.error || error ? (
          <div role="alert" className="alert alert-error">
            {safeErrorMessage(query.error || error, 'Unable to load agreement')}
          </div>
        ) : null}
        {doc ? (
          <>
            <AgreementDocumentContent document={doc} />
            <div className="agreement-actions no-print">
              <button className="btn" onClick={() => window.print()}>
                Print / Save PDF
              </button>
              {(!token && !readOnly && doc.kind !== 'completion') || canSign ? (
                <label>
                  Customer email
                  <input
                    type="email"
                    className="input"
                    value={doc.recipient_email || email}
                    disabled={!!doc.recipient_email || busy}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </label>
              ) : null}
              {canSign ? (
                <button
                  className="btn primary"
                  disabled={busy || !(doc.recipient_email || email)}
                  onClick={() => setSigning(true)}
                >
                  Sign {doc.kind === 'completion' ? 'completion' : 'agreement'}
                </button>
              ) : null}
              {!token && !readOnly && doc.kind !== 'completion' ? (
                <button
                  className="btn"
                  disabled={busy || !email || !linkReady}
                  onClick={async () => {
                    setBusy(true);
                    setError('');
                    try {
                      setLink(await createAgreementLink(doc.kind, doc.id, email));
                    } catch (e) {
                      setError(safeErrorMessage(e, 'Could not create link'));
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  Create secure link
                </button>
              ) : null}
              {!token && !readOnly && !linkReady ? (
                <p className="agreement-link-hint">The secure link is available once the quote is approved.</p>
              ) : null}
              {link ? (
                <label>
                  Secure link to share
                  <input className="input" value={link} readOnly onFocus={(e) => e.target.select()} />
                  <a href={link} target="_blank" rel="noreferrer">
                    Preview customer link
                  </a>
                </label>
              ) : null}
            </div>
            <SignatureModal
              open={signing}
              busy={busy}
              title={
                doc.kind === 'completion'
                  ? 'Accept completed installation'
                  : 'Accept service agreement'
              }
              requireSignerNameForDrawn
              acceptanceStatement={
                doc.kind === 'completion'
                  ? doc.terms
                  : 'I accept this service agreement, its coverage dates, price, benefits, and terms.'
              }
              onCancel={() => setSigning(false)}
              onConfirm={async (signature) => {
                setBusy(true);
                setError('');
                try {
                  await signAgreementDocument(
                    doc.kind,
                    doc.id,
                    signature,
                    doc.recipient_email || email,
                    token,
                  );
                  setSigning(false);
                  await query.refetch();
                } catch (e) {
                  setError(safeErrorMessage(e, 'Could not record acceptance'));
                } finally {
                  setBusy(false);
                }
              }}
            />
          </>
        ) : null}
      </section>
    </>
  );
}
